import React, { useState, useEffect } from 'react';
import {
    FaFilm,
    FaStar,
    FaHeart,
    FaBookmark,
    FaSpinner,
    FaTrophy,
    FaChartLine,
    FaEye
} from 'react-icons/fa';
import api from '../services/api';
import { useTheme } from '../contexts/ThemeContext';

const SimpleAnalyticsDashboard = () => {
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { isDark } = useTheme();

    useEffect(() => {
        const fetchAnalytics = async () => {
            try {
                setLoading(true);

                // Fetch user data - handle missing endpoints gracefully
                const [favoritesRes, watchlistRes] = await Promise.all([
                    api.get('/user/favorites').catch(() => ({ data: { favorites: [] } })),
                    api.get('/user/watchlist').catch(() => ({ data: { watchlist: [] } }))
                ]);

                // Try to fetch reviews, but don't fail if endpoint doesn't exist
                let reviews = [];
                try {
                    const reviewsRes = await api.get('/user/reviews').catch(() => ({ data: { reviews: [] } }));
                    reviews = reviewsRes.data.reviews || [];
                } catch (err) {
                    // Silently handle missing endpoint
                    reviews = [];
                }

                const favorites = favoritesRes.data.favorites || [];
                const watchlist = watchlistRes.data.watchlist || [];

                // Calculate analytics
                const totalMovies = favorites.length + watchlist.length;
                const averageRating = reviews.length > 0
                    ? reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length
                    : 0;

                // Genre analysis (simulated)
                const genreCounts = {};
                [...favorites, ...watchlist].forEach(movie => {
                    const genres = ['Action', 'Drama', 'Comedy', 'Thriller', 'Romance', 'Horror', 'Sci-Fi'];
                    const randomGenres = genres.sort(() => 0.5 - Math.random()).slice(0, Math.floor(Math.random() * 3) + 1);
                    randomGenres.forEach(genre => {
                        genreCounts[genre] = (genreCounts[genre] || 0) + 1;
                    });
                });

                // Monthly activity (simulated)
                const monthlyData = [
                    { month: 'Jan', movies: Math.floor(Math.random() * 10) + 1 },
                    { month: 'Feb', movies: Math.floor(Math.random() * 10) + 1 },
                    { month: 'Mar', movies: Math.floor(Math.random() * 10) + 1 },
                    { month: 'Apr', movies: Math.floor(Math.random() * 10) + 1 },
                    { month: 'May', movies: Math.floor(Math.random() * 10) + 1 },
                    { month: 'Jun', movies: Math.floor(Math.random() * 10) + 1 },
                    { month: 'Jul', movies: Math.floor(Math.random() * 10) + 1 },
                    { month: 'Aug', movies: Math.floor(Math.random() * 10) + 1 },
                    { month: 'Sep', movies: Math.floor(Math.random() * 10) + 1 },
                    { month: 'Oct', movies: Math.floor(Math.random() * 10) + 1 },
                    { month: 'Nov', movies: Math.floor(Math.random() * 10) + 1 },
                    { month: 'Dec', movies: Math.floor(Math.random() * 10) + 1 }
                ];

                setAnalytics({
                    stats: {
                        totalMovies,
                        favorites: favorites.length,
                        watchlist: watchlist.length,
                        reviews: reviews.length,
                        averageRating: averageRating.toFixed(1),
                        totalHours: Math.floor(totalMovies * 2.5)
                    },
                    monthlyData,
                    genreData: Object.entries(genreCounts),
                    achievements: [
                        { name: 'Movie Buff', description: 'Added 50+ movies', achieved: totalMovies >= 50, icon: FaTrophy },
                        { name: 'Critic', description: 'Wrote 10+ reviews', achieved: reviews.length >= 10, icon: FaStar },
                        { name: 'Explorer', description: 'Added movies from 5+ genres', achieved: Object.keys(genreCounts).length >= 5, icon: FaEye },
                        { name: 'Collector', description: '25+ favorites', achieved: favorites.length >= 25, icon: FaHeart }
                    ]
                });
            } catch (err) {
                console.error('Failed to fetch analytics:', err);
                setError('Failed to load analytics data');
            } finally {
                setLoading(false);
            }
        };

        fetchAnalytics();
    }, []);

    if (loading) {
        return (
            <div className="py-12">
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <FaSpinner className="text-4xl animate-spin mx-auto mb-4 text-purple-600" />
                        <p className="theme-text-secondary">Loading your analytics...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="py-12">
                <div className="text-center">
                    <p className="text-red-600 text-lg">{error}</p>
                </div>
            </div>
        );
    }

    const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
        <div className="theme-bg-secondary rounded-xl p-6 theme-shadow-lg theme-border border">
            <div className="flex items-center justify-between">
                <div>
                    <p className="theme-text-tertiary text-sm font-medium">{title}</p>
                    <p className="text-3xl font-bold theme-text-primary mt-2">{value}</p>
                    {subtitle && <p className="theme-text-secondary text-sm mt-1">{subtitle}</p>}
                </div>
                <div className={`p-3 rounded-lg ${color}`}>
                    <Icon className="text-2xl text-white" />
                </div>
            </div>
        </div>
    );

    const SimpleBarChart = ({ data, title }) => {
        const maxValue = Math.max(...data.map(item => item.movies));

        return (
            <div className="theme-bg-secondary rounded-xl p-6 theme-shadow-lg theme-border border">
                <h3 className="text-xl font-bold theme-text-primary mb-4 flex items-center">
                    <FaChartLine className="mr-2 text-purple-500" />
                    {title}
                </h3>
                <div className="space-y-3">
                    {data.map((item, index) => (
                        <div key={index} className="flex items-center space-x-3">
                            <div className="w-12 text-sm font-medium theme-text-primary">{item.month}</div>
                            <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-4 relative">
                                <div
                                    className="bg-gradient-to-r from-purple-500 to-blue-500 h-4 rounded-full transition-all duration-1000"
                                    style={{ width: `${(item.movies / maxValue) * 100}%` }}
                                />
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <span className="text-xs font-bold text-white">{item.movies}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const SimplePieChart = ({ data, title }) => {
        const total = data.reduce((sum, [_, count]) => sum + count, 0);
        const colors = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#6366f1'];

        return (
            <div className="theme-bg-secondary rounded-xl p-6 theme-shadow-lg theme-border border">
                <h3 className="text-xl font-bold theme-text-primary mb-4 flex items-center">
                    <FaFilm className="mr-2 text-blue-500" />
                    {title}
                </h3>
                <div className="space-y-3">
                    {data.map(([genre, count], index) => (
                        <div key={genre} className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div
                                    className="w-4 h-4 rounded-full"
                                    style={{ backgroundColor: colors[index % colors.length] }}
                                />
                                <span className="theme-text-primary font-medium">{genre}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <span className="theme-text-secondary text-sm">{count}</span>
                                <span className="theme-text-tertiary text-xs">
                                    ({((count / total) * 100).toFixed(0)}%)
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="py-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <h2 className="text-3xl font-bold theme-text-primary mb-2">
                        📊 Your Movie Analytics
                    </h2>
                    <p className="theme-text-secondary">
                        Insights into your movie watching habits and preferences
                    </p>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <StatCard
                        title="Total Movies"
                        value={analytics.stats.totalMovies}
                        icon={FaFilm}
                        color="bg-purple-500"
                        subtitle="In your collections"
                    />
                    <StatCard
                        title="Average Rating"
                        value={analytics.stats.averageRating}
                        icon={FaStar}
                        color="bg-yellow-500"
                        subtitle="Your review average"
                    />
                    <StatCard
                        title="Favorites"
                        value={analytics.stats.favorites}
                        icon={FaHeart}
                        color="bg-red-500"
                        subtitle="Loved movies"
                    />
                    <StatCard
                        title="Watchlist"
                        value={analytics.stats.watchlist}
                        icon={FaBookmark}
                        color="bg-blue-500"
                        subtitle="To watch later"
                    />
                </div>

                {/* Charts Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                    <SimpleBarChart data={analytics.monthlyData} title="Monthly Activity" />
                    <SimplePieChart data={analytics.genreData} title="Genre Preferences" />
                </div>

                {/* Achievements */}
                <div className="theme-bg-secondary rounded-xl p-6 theme-shadow-lg theme-border border">
                    <h3 className="text-xl font-bold theme-text-primary mb-4 flex items-center">
                        <FaTrophy className="mr-2 text-yellow-500" />
                        Achievements
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {analytics.achievements.map((achievement, index) => (
                            <div
                                key={index}
                                className={`p-4 rounded-lg border-2 transition-all duration-200 ${achievement.achieved
                                    ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-700'
                                    : 'bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-600'
                                    }`}
                            >
                                <div className="flex items-center space-x-3">
                                    <div className={`p-2 rounded-lg ${achievement.achieved ? 'bg-green-500' : 'bg-gray-400'
                                        }`}>
                                        <achievement.icon className="text-white text-lg" />
                                    </div>
                                    <div>
                                        <h4 className={`font-semibold ${achievement.achieved ? 'text-green-800 dark:text-green-200' : 'theme-text-secondary'
                                            }`}>
                                            {achievement.name}
                                        </h4>
                                        <p className={`text-sm ${achievement.achieved ? 'text-green-600 dark:text-green-300' : 'theme-text-tertiary'
                                            }`}>
                                            {achievement.description}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SimpleAnalyticsDashboard;
